import {IAppError} from "@pg-mono/request-state";

import {hitApplicationRecommendedPropertiesError, hitApplicationRecommendedPropertiesSuccess} from "../../../tracking/algolytics_hits/application_hit";
import {modalCloseClickHit} from "../../../tracking/algolytics_hits/modal_close_click_hit";
import {multiLeadModalCheckboxClickHit, multileadPinClickHit} from "../../../tracking/algolytics_hits/multilead_modal_checkbox_click_hit";
import {awinHit} from "../../../tracking/awin_tracking/awin_conversion_tag";
import {
    gtmMultileadButtonClick,
    gtmMultileadModalCheckboxClick,
    gtmMultileadModalClose,
    gtmMultileadModalOpen,
    gtmMultileadPinClick,
    gtmMultileadSend,
    MultileadButtonType
} from "../../../tracking/google_tag_manager/gtm_event_multilead";
import {Currency} from "../../../types/Currency";
import {ViewType} from "../../../view_type/ViewType";
import {hitOfferMultileadSent} from "../../tracking/lead/hit_offer_multilead_sent";
import {hitPropertyMultileadSent} from "../../tracking/lead/hit_property_multilead_sent";
import {IApplicationData} from "../../types/IApplicationData";
import {ILeadModalLeadType} from "../../types/lead/ILeadModalLeadType";
import {ApplicationMultiLeadRecommendationType} from "../../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {ApplicationMultiLeadRecommendedPropertyType} from "../../types/multi_lead/ApplicationMultiLeadRecommendedPropertyType";
import {ApplicationMultiLeadTracking} from "../../types/multi_lead/ApplicationMultiLeadTracking";
import {ApplicationSource, ApplicationSourceSection} from "../../utils/form/application_form_source";
import {RecommendedOfferApplicationsType, useTrackRecommendedOffersApplication} from "./use_track_recommended_offers_application";
import {useTrackRecommendedPropertiesApplication} from "./use_track_recommended_properties_application";

const isProperty = (type: ApplicationMultiLeadRecommendationType) => type === ApplicationMultiLeadRecommendationType.PROPERTY;
const isOffer = (type: ApplicationMultiLeadRecommendationType) => type === ApplicationMultiLeadRecommendationType.OFFER;
const isVendor = (type: ApplicationMultiLeadRecommendationType) => type === ApplicationMultiLeadRecommendationType.VENDOR;

export interface MultiLeadSendTrackingData {
    selectedCount: number;
    totalOffers: number;
    buttonText: string;
}

export const useMultiLeadReport = (customGtmTracking?: ApplicationMultiLeadTracking) => {
    const trackRecommendedPropertiesApplication = useTrackRecommendedPropertiesApplication();
    const trackRecommendedOffersApplication = useTrackRecommendedOffersApplication();

    const checkboxChange = (
        recommendationType: ApplicationMultiLeadRecommendationType,
        viewType: ViewType,
        elementSelected: boolean,
        recommendedElementId: number
    ) => {
        gtmMultileadModalCheckboxClick(elementSelected, recommendedElementId);
        multiLeadModalCheckboxClickHit(viewType, elementSelected, recommendedElementId, recommendationType);
    };

    const modalClose = (recommendationType: ApplicationMultiLeadRecommendationType, viewType: ViewType | null, totalOffers?: number) => {
        if (customGtmTracking?.onClose && totalOffers !== undefined) {
            customGtmTracking.onClose(totalOffers);
        } else {
            gtmMultileadModalClose();
        }
        modalCloseClickHit(viewType, "recommended_offers", recommendationType);
    };

    const modalOpen = (totalOffers?: number, buttonText?: string) => {
        if (customGtmTracking?.onView && totalOffers !== undefined && buttonText !== undefined) {
            customGtmTracking.onView(totalOffers, buttonText);
        } else {
            gtmMultileadModalOpen();
        }
    };

    const finishedSending = (
        recommendationType: ApplicationMultiLeadRecommendationType,
        applications: ApplicationMultiLeadRecommendedPropertyType[] | RecommendedOfferApplicationsType[],
        viewType: ViewType | null,
        leadType: ILeadModalLeadType,
        originalApplication: IApplicationData | null | undefined,
        recommendationsWithPriceFilter = false,
        customSourceSection?: string,
        currency?: Currency,
        sendTrackingData?: MultiLeadSendTrackingData
    ) => {
        if (recommendationType !== ApplicationMultiLeadRecommendationType.VENDOR) {
            if (customGtmTracking?.onSend && sendTrackingData) {
                customGtmTracking.onSend(sendTrackingData.selectedCount, sendTrackingData.totalOffers, sendTrackingData.buttonText);
            } else if (!customGtmTracking) {
                gtmMultileadSend();
                gtmMultileadButtonClick(MultileadButtonType.SEND_APPLICATIONS);
            }
        }

        if (isProperty(recommendationType)) {
            hitPropertyMultileadSent(applications as ApplicationMultiLeadRecommendedPropertyType[], viewType, recommendationType, originalApplication);
            trackRecommendedPropertiesApplication(
                applications as ApplicationMultiLeadRecommendedPropertyType[],
                viewType,
                leadType,
                recommendationsWithPriceFilter,
                customSourceSection
            );
        }

        if (isOffer(recommendationType)) {
            hitOfferMultileadSent(applications as RecommendedOfferApplicationsType[], viewType, recommendationType, originalApplication);
            trackRecommendedOffersApplication(applications as RecommendedOfferApplicationsType[], viewType, leadType);
        }

        if (isVendor(recommendationType)) {
            hitPropertyMultileadSent(applications as ApplicationMultiLeadRecommendedPropertyType[], viewType, recommendationType, originalApplication);
            trackRecommendedPropertiesApplication(
                applications as ApplicationMultiLeadRecommendedPropertyType[],
                viewType,
                leadType,
                recommendationsWithPriceFilter,
                customSourceSection
            );
        }

        applications.forEach((application) => {
            awinHit({
                applicationId: application[0].uuid,
                currency: currency
            });
        });
    };

    const singleApplicationSuccess = (application: IApplicationData, sourceSection: ApplicationSourceSection, viewType: ViewType | null) => {
        hitApplicationRecommendedPropertiesSuccess(application, sourceSection, {
            extraPayloadViewType: viewType ?? undefined
        });
    };

    const singleApplicationError = (appError: IAppError, source: ApplicationSource, sourceSection: ApplicationSourceSection, viewType: ViewType | null) => {
        hitApplicationRecommendedPropertiesError(source, sourceSection, {
            appError: appError as IAppError,
            extraPayloadViewType: viewType ?? undefined
        });
    };

    const mapPinClick = (viewType: ViewType | null, recommendedElementId: number, elementSelected: boolean) => {
        gtmMultileadPinClick();
        multileadPinClickHit(viewType, recommendedElementId, elementSelected);
    };

    return {
        checkboxChange,
        modalOpen,
        modalClose,
        finishedSending,
        singleApplicationSuccess,
        singleApplicationError,
        mapPinClick
    };
};
import {IAppError} from "@pg-mono/request-state";

import {hitApplicationRecommendedPropertiesError, hitApplicationRecommendedPropertiesSuccess} from "../../../tracking/algolytics_hits/application_hit";
import {modalCloseClickHit} from "../../../tracking/algolytics_hits/modal_close_click_hit";
import {multiLeadModalCheckboxClickHit, multileadPinClickHit} from "../../../tracking/algolytics_hits/multilead_modal_checkbox_click_hit";
import {awinHit} from "../../../tracking/awin_tracking/awin_conversion_tag";
import {
    gtmMultileadButtonClick,
    gtmMultileadModalCheckboxClick,
    gtmMultileadModalClose,
    gtmMultileadModalOpen,
    gtmMultileadPinClick,
    gtmMultileadSend,
    MultileadButtonType
} from "../../../tracking/google_tag_manager/gtm_event_multilead";
import {Currency} from "../../../types/Currency";
import {ViewType} from "../../../view_type/ViewType";
import {hitOfferMultileadSent} from "../../tracking/lead/hit_offer_multilead_sent";
import {hitPropertyMultileadSent} from "../../tracking/lead/hit_property_multilead_sent";
import {IApplicationData} from "../../types/IApplicationData";
import {ILeadModalLeadType} from "../../types/lead/ILeadModalLeadType";
import {ApplicationMultiLeadRecommendationType} from "../../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {ApplicationMultiLeadRecommendedPropertyType} from "../../types/multi_lead/ApplicationMultiLeadRecommendedPropertyType";
import {ApplicationMultiLeadTracking} from "../../types/multi_lead/ApplicationMultiLeadTracking";
import {ApplicationSource, ApplicationSourceSection} from "../../utils/form/application_form_source";
import {RecommendedOfferApplicationsType, useTrackRecommendedOffersApplication} from "./use_track_recommended_offers_application";
import {useTrackRecommendedPropertiesApplication} from "./use_track_recommended_properties_application";

const isProperty = (type: ApplicationMultiLeadRecommendationType) => type === ApplicationMultiLeadRecommendationType.PROPERTY;
const isOffer = (type: ApplicationMultiLeadRecommendationType) => type === ApplicationMultiLeadRecommendationType.OFFER;
const isVendor = (type: ApplicationMultiLeadRecommendationType) => type === ApplicationMultiLeadRecommendationType.VENDOR;

export interface MultiLeadSendTrackingData {
    selectedCount: number;
    totalOffers: number;
    buttonText: string;
}

export const useMultiLeadReport = (customGtmTracking?: ApplicationMultiLeadTracking) => {
    const trackRecommendedPropertiesApplication = useTrackRecommendedPropertiesApplication();
    const trackRecommendedOffersApplication = useTrackRecommendedOffersApplication();

    const checkboxChange = (
        recommendationType: ApplicationMultiLeadRecommendationType,
        viewType: ViewType,
        elementSelected: boolean,
        recommendedElementId: number
    ) => {
        gtmMultileadModalCheckboxClick(elementSelected, recommendedElementId);
        multiLeadModalCheckboxClickHit(viewType, elementSelected, recommendedElementId, recommendationType);
    };

    const modalClose = (recommendationType: ApplicationMultiLeadRecommendationType, viewType: ViewType | null, totalOffers?: number) => {
        if (customGtmTracking?.onClose && totalOffers !== undefined) {
            customGtmTracking.onClose(totalOffers);
        } else {
            gtmMultileadModalClose();
        }
        modalCloseClickHit(viewType, "recommended_offers", recommendationType);
    };

    const modalOpen = (totalOffers?: number, buttonText?: string) => {
        if (customGtmTracking?.onView && totalOffers !== undefined && buttonText !== undefined) {
            customGtmTracking.onView(totalOffers, buttonText);
        } else {
            gtmMultileadModalOpen();
        }
    };

    const finishedSending = (
        recommendationType: ApplicationMultiLeadRecommendationType,
        applications: ApplicationMultiLeadRecommendedPropertyType[] | RecommendedOfferApplicationsType[],
        viewType: ViewType | null,
        leadType: ILeadModalLeadType,
        originalApplication: IApplicationData | null | undefined,
        recommendationsWithPriceFilter = false,
        customSourceSection?: string,
        currency?: Currency,
        sendTrackingData?: MultiLeadSendTrackingData
    ) => {
        if (recommendationType !== ApplicationMultiLeadRecommendationType.VENDOR) {
            if (customGtmTracking?.onSend && sendTrackingData) {
                customGtmTracking.onSend(sendTrackingData.selectedCount, sendTrackingData.totalOffers, sendTrackingData.buttonText);
            } else if (!customGtmTracking) {
                gtmMultileadSend();
                gtmMultileadButtonClick(MultileadButtonType.SEND_APPLICATIONS);
            }
        }

        if (isProperty(recommendationType)) {
            hitPropertyMultileadSent(applications as ApplicationMultiLeadRecommendedPropertyType[], viewType, recommendationType, originalApplication);
            trackRecommendedPropertiesApplication(
                applications as ApplicationMultiLeadRecommendedPropertyType[],
                viewType,
                leadType,
                recommendationsWithPriceFilter,
                customSourceSection
            );
        }

        if (isOffer(recommendationType)) {
            hitOfferMultileadSent(applications as RecommendedOfferApplicationsType[], viewType, recommendationType, originalApplication);
            trackRecommendedOffersApplication(applications as RecommendedOfferApplicationsType[], viewType, leadType);
        }

        if (isVendor(recommendationType)) {
            hitPropertyMultileadSent(applications as ApplicationMultiLeadRecommendedPropertyType[], viewType, recommendationType, originalApplication);
            trackRecommendedPropertiesApplication(
                applications as ApplicationMultiLeadRecommendedPropertyType[],
                viewType,
                leadType,
                recommendationsWithPriceFilter,
                customSourceSection
            );
        }

        applications.forEach((application) => {
            awinHit({
                applicationId: application[0].uuid,
                currency: currency
            });
        });
    };

    const singleApplicationSuccess = (application: IApplicationData, sourceSection: ApplicationSourceSection, viewType: ViewType | null) => {
        hitApplicationRecommendedPropertiesSuccess(application, sourceSection, {
            extraPayloadViewType: viewType ?? undefined
        });
    };

    const singleApplicationError = (appError: IAppError, source: ApplicationSource, sourceSection: ApplicationSourceSection, viewType: ViewType | null) => {
        hitApplicationRecommendedPropertiesError(source, sourceSection, {
            appError: appError as IAppError,
            extraPayloadViewType: viewType ?? undefined
        });
    };

    const mapPinClick = (viewType: ViewType | null, recommendedElementId: number, elementSelected: boolean) => {
        gtmMultileadPinClick();
        multileadPinClickHit(viewType, recommendedElementId, elementSelected);
    };

    return {
        checkboxChange,
        modalOpen,
        modalClose,
        finishedSending,
        singleApplicationSuccess,
        singleApplicationError,
        mapPinClick
    };
};
